import React, { useState } from "react";
import { Popover, IconButton, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { LastOrderDetails } from "../types";
import shared from '@/components/utils/shared.module.css';

const fmt = (v: number | null | undefined): string => {
  if (v == null) return "—";
  return v % 1 === 0 ? String(v) : v.toFixed(2);
};

interface FieldRow {
  label: string;
  key: keyof LastOrderDetails;
  format?: (v: number | null | undefined) => string;
}

const BUY_FIELDS: FieldRow[] = [
  { label: "Date", key: "order_date" },
  { label: "Type", key: "order_type_display" },
  { label: "Limit Price", key: "limit_price" },
  { label: "Inc To 1", key: "increase_to_1" },
  { label: "Inc To 2", key: "increase_to_2" },
  { label: "Inc Max 1", key: "increment_max_1" },
  { label: "Inc Max 2", key: "increment_max_2" },
  { label: "Inc By 1", key: "increase_by_1" },
  { label: "Inc By 2", key: "increase_by_2" },
  { label: "Max Holding %", key: "max_holding" },
  { label: "Min Order Val", key: "min_order_val" },
  { label: "Method", key: "method_display" },
  { label: "POV CAP %", key: "median_volume_pct" },
  { label: "Apply Vol Cap", key: "apply_volume_cap" },
];

const SELL_FIELDS: FieldRow[] = [
  { label: "Date", key: "order_date" },
  { label: "Type", key: "order_type_display" },
  { label: "H1", key: "h1" },
  { label: "H2", key: "h2" },
  { label: "Condition 1", key: "condition_1" },
  { label: "Condition 2", key: "condition_2" },
  { label: "Min Order Val", key: "min_order_val" },
  { label: "Method", key: "method_display" },
  { label: "POV CAP %", key: "median_volume_pct" },
  { label: "Apply Vol Cap", key: "apply_volume_cap" },
];

const renderCellValue = (
  field: FieldRow,
  order: LastOrderDetails | null | undefined,
): string => {
  if (!order) return "—";
  const value = order[field.key];
  if (value == null) return "—";
  if (field.key === "order_date" && typeof value === "string") {
    return new Date(value).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return fmt(value);
  return String(value);
};

interface Props {
  ordersByStrategy: Record<string, LastOrderDetails | null>;
  isMismatch: boolean;
  isBuy: boolean;
}

export const LastOrderDetailsPopover: React.FC<Props> = ({
  ordersByStrategy,
  isMismatch,
  isBuy,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const codes = Object.keys(ordersByStrategy);
  const hasAnyOrder = codes.some((c) => ordersByStrategy[c] != null);

  if (!hasAnyOrder) return null;

  const fieldRows = isBuy ? BUY_FIELDS : SELL_FIELDS;
  const TriggerIcon = isMismatch ? WarningAmberIcon : InfoOutlinedIcon;
  const triggerColor = isMismatch ? "#d97706" : "#1565c0";
  const triggerTooltip = isMismatch ? "Sub-strategy orders disagree — click for details" : "Last Order Details";

  return (
    <>
      <Tooltip title={triggerTooltip}>
        <IconButton
          size="small"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{ padding: "2px", color: triggerColor }}
        >
          <TriggerIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <div style={{ padding: 8, fontSize: 12, minWidth: 240 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Last Order by Sub-Strategy</div>
          <table className={shared.table} style={{ borderCollapse: "collapse", width: "100%" }}>
            <thead>
              <tr>
                <th style={{ textAlign: "left", padding: "4px 6px", borderBottom: "1px solid #e5e7eb" }}>Field</th>
                {codes.map((code) => (
                  <th
                    key={code}
                    style={{ textAlign: "right", padding: "4px 6px", borderBottom: "1px solid #e5e7eb" }}
                  >
                    {code}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {fieldRows.map((field) => {
                const values = codes.map((c) => renderCellValue(field, ordersByStrategy[c]));
                const presentValues = values.filter((v) => v !== "—");
                const diff = new Set(presentValues).size > 1;
                return (
                  <tr key={field.label} style={{ background: diff ? "#fff3cd" : undefined }}>
                    <td style={{ padding: "3px 6px", color: "#475569" }}>{field.label}</td>
                    {values.map((v, i) => (
                      <td key={codes[i]} style={{ padding: "3px 6px", textAlign: "right" }}>{v}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Popover>
    </>
  );
};
